import { TableInvoiceRow } from "../types/invoice-table.types";

export const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

export interface VATBreakdown {
	taxRate: number;
	netAmount: number;
	vatAmount: number;
	grossAmount: number;
	invoiceCount: number;
	currency: string;
	invoiceValue: number;
}

export interface VATGrandTotals {
	netAmount: number;
	vatAmount: number;
	grossAmount: number;
	invoiceCount: number;
	currency: string;
}

export type VATCalculationMode = 'full' | 'paid' | 'open';

export function calculateVATBreakdown(invoiceData: TableInvoiceRow[], mode: VATCalculationMode = 'paid'): VATBreakdown[] {
	const breakdown = new Map<number, VATBreakdown>();

	invoiceData.forEach(invoice => {
		const taxRate = invoice.effective_tax_rate || 0;
		const invoiceValue = invoice.invoice_value || 0;
		const paidAmount = (invoice.total_allocated_amount || 0);
		const openAmount = invoiceValue - paidAmount;
		const currency = invoice.invoice_currency || 'CHF';
		const fullVatAmount = invoice.vat_amount || 0;

		let grossAmount: number;
		let vatAmount: number;
		let netAmount: number;

		switch (mode) {
			case 'full':
				grossAmount = invoiceValue;
				vatAmount = fullVatAmount;
				netAmount = invoiceValue - fullVatAmount;
				break;
			case 'open':
				const openRatio = invoiceValue > 0 ? openAmount / invoiceValue : 0;
				grossAmount = openAmount;
				vatAmount = fullVatAmount * openRatio;
				netAmount = grossAmount - vatAmount;
				break;
			case 'paid':
			default:
				const paidRatio = invoiceValue > 0 ? paidAmount / invoiceValue : 0;
				grossAmount = paidAmount;
				vatAmount = fullVatAmount * paidRatio;
				netAmount = grossAmount - vatAmount;
				break;
		}

		grossAmount = (grossAmount);
		vatAmount = roundCurrency(vatAmount);
		netAmount = roundCurrency(netAmount);

		if (breakdown.has(taxRate)) {
			const existing = breakdown.get(taxRate)!;
			existing.netAmount += netAmount;
			existing.vatAmount += vatAmount;
			existing.grossAmount += grossAmount;
			existing.invoiceCount += 1;
			existing.invoiceValue += (invoiceValue);
		} else {
			breakdown.set(taxRate, {
				taxRate,
				netAmount,
				vatAmount,
				grossAmount,
				invoiceCount: 1,
				currency,
				invoiceValue: (invoiceValue),
			});
		}
	});

	return Array.from(breakdown.values()).sort((a, b) => a.taxRate - b.taxRate);
}

export function calculateVATGrandTotals(vatBreakdown: VATBreakdown[]): VATGrandTotals {
	return vatBreakdown.reduce(
		(totals, item) => ({
			netAmount: totals.netAmount + item.netAmount,
			vatAmount: totals.vatAmount + item.vatAmount,
			grossAmount: totals.grossAmount + item.grossAmount,
			invoiceCount: totals.invoiceCount + item.invoiceCount,
			currency: item.currency,
		}),
		{ netAmount: 0, vatAmount: 0, grossAmount: 0, invoiceCount: 0, currency: 'CHF' }
	);
}
